import { useCallback, useEffect, useRef, useState } from 'react'
import { postSubmit } from '@ui-seeker/utils'

import { BACKEND_APIS } from '@constants'
import { createLatestGate, shouldPaint } from '../../../../../../@components/KindPillsField/latestOnly'
import { curveKey } from '../../../@services/curve'


// the session's curves: reopening a cell is instant. The statistic and Tiers / Providers are not in the key (they are
// composed in the front); an error is never cached
const CACHE = new Map()

/**
 * The price curve of the cell being looked at (CU-869fmhmx8 E9): asks when the detail opens, paints only the latest
 * answer, ignores a cancel (closing the detail) and lets "Try again" ask again. `body` null = nothing open.
 */
export default function useCellCurve(body) {
	const key = body ? curveKey(body) : null
	const [state, setState] = useState(() => ({ key, data: key ? CACHE.get(key) || null : null, loading: false, error: null }))
	const gate = useRef(createLatestGate())
	const [attempt, setAttempt] = useState(0)

	useEffect(() => {
		if (!key) return undefined
		if (CACHE.has(key)) {
			setState({ key, data: CACHE.get(key), loading: false, error: null })
			return undefined
		}
		const ctrl = new AbortController()
		const id = gate.current.next()
		setState({ key, data: null, loading: true, error: null })
		postSubmit(`${BACKEND_APIS.DASHBOARD}/results`, JSON.parse(key), { signal: ctrl.signal, retries: 1 })
			.then(data => {
				CACHE.set(key, data)
				if (shouldPaint(gate.current, id)) setState({ key, data, loading: false, error: null })
			})
			.catch(error => {
				if (shouldPaint(gate.current, id, error)) setState({ key, data: null, loading: false, error: error || new Error('request failed') })
			})
		return () => ctrl.abort()
	}, [key, attempt])

	const retry = useCallback(() => setAttempt(n => n + 1), [])

	// a state of another cell is never shown while the new one loads
	const current = state.key === key ? state : { key, data: null, loading: !!key, error: null }
	return { data: current.data, loading: current.loading, error: current.error, retry }
}
